import React, { createContext, useContext, useState, useCallback } from 'react';

const SolutionModelContext = createContext();

export const useSolutionModel = () => {
  // console.log("useSolutionModel :: ",SolutionModelContext);
  const context = useContext(SolutionModelContext);
  if (!context) {
    throw new Error('useSolutionModel must be used within a SolutionModelProvider');
  }
  return context;
};

export const SolutionModelProvider = ({ children }) => {
  const [solutionModelList, setSolutionModelList] = useState([]);
  const [previousValues, setPreviousValues] = useState({}); // Store previous values by sol_mod_id
  const [showPreviousValues, setShowPreviousValues] = useState({}); // Track which sol_mod_ids should show previous values
  const [lastImpactedId, setLastImpactedId] = useState(null); // Track the last impacted sol_mod_id for scroll
  const [hiddenRows, setHiddenRows] = useState({}); // Track which rows should be hidden (for "add" type impacts)
  const [removedRows, setRemovedRows] = useState({}); // Track removed rows that should be shown (for "remove" type impacts)

  // --- Highlighted sol_mod_ids for review change (not impact) ---
  const [highlightSolModIds, setHighlightSolModIds] = useState([]);
  // --- Highlighted sol_mod_ids for review change (not impact) ---

  // Add a solution model with impact type to the list
  const addSolutionModel = useCallback((solModId, impact) => {
    const idString = String(solModId);
    setSolutionModelList(prev => {
      if (!prev.find(item => item.sol_mod_id === idString)) {
        return [...prev, { sol_mod_id: idString, impact }];
      }
      return prev;
    });
  }, []);

  // Remove a solution model from the list
  const removeSolutionModel = useCallback((solModId) => {
    const idString = String(solModId);
    setSolutionModelList(prev => prev.filter(item => item.sol_mod_id !== idString));
    // Also remove from showPreviousValues when removing from list
    setShowPreviousValues(prev => {
      const newState = { ...prev };
      delete newState[idString];
      return newState;
    });
  }, []);

  // Clear the entire list
  const clearSolutionModelList = useCallback(() => {
    setSolutionModelList([]);
    setShowPreviousValues({});
    setLastImpactedId(null);
  }, []);

  // Check if a solution model is in the list
  const isSolutionModelSelected = useCallback((solModId) => {
    // console.log("isSolutionModelSelected :: ",solModId);
    const idString = String(solModId);
    return solutionModelList.some(item => item.sol_mod_id === idString);
  }, [solutionModelList]);

  // Get the impact type for a solution model
  const getSolutionModelImpact = useCallback((solModId) => {
    const idString = String(solModId);
    const item = solutionModelList.find(item => item.sol_mod_id === idString);
    return item ? item.impact : null;
  }, [solutionModelList]);

  // Get the current list
  const getSolutionModelList = useCallback(() => {
    return solutionModelList;
  }, [solutionModelList]);

  // Set previous values data
  const setPreviousValuesData = useCallback((data) => {
    setPreviousValues(data);
  }, []);

  // Toggle showing previous values for a specific sol_mod_id
  const togglePreviousValues = useCallback((solModId) => {
    const idString = String(solModId);
    // console.log("togglePreviousValues :: ",showPreviousValues);
    setShowPreviousValues(prev => ({
      ...prev,
      [idString]: !prev[idString]
    }));
  }, []);

  // Check if previous values should be shown for a sol_mod_id
  const shouldShowPreviousValues = useCallback((solModId) => {
    const idString = String(solModId);
    return showPreviousValues[idString] || false;
  }, [showPreviousValues]);

  // Get previous value for a specific sol_mod_id
  const getPreviousValue = useCallback((solModId) => {
    // console.log("getPreviousValue :: ",solModId);
    const idString = String(solModId);
    return previousValues[idString] || null;
  }, [previousValues]);

  // Set impact highlight for scroll functionality
  const setImpactHighlight = useCallback((solModId) => {
    const idString = String(solModId);
    console.log('🔍 SolutionModelContext - setImpactHighlight called with:', idString);
    setLastImpactedId(idString);
    
    // Add to highlightSolModIds array for table highlighting
    setHighlightSolModIds(prev => {
      console.log('🔍 Current highlightSolModIds:', prev);
      if (!prev.includes(idString)) {
        const newArray = [...prev, idString];
        console.log('✅ Adding to highlightSolModIds:', newArray);
        return newArray;
      }
      console.log('ℹ️ sol_mod_id already in highlightSolModIds');
      return prev;
    });
    
    // Dispatch custom event for other components to listen to
    const event = new CustomEvent('impactHighlight', { detail: idString });
    window.dispatchEvent(event);
  }, []);

  // Clear all highlights
  const clearHighlights = useCallback(() => {
    console.log('🔍 SolutionModelContext - clearHighlights called');
    setHighlightSolModIds([]);
  }, []);

  // Hide a row (for "add" type impacts that are rejected)
  const hideRow = useCallback((solModId) => {
    const idString = String(solModId);
    setHiddenRows(prev => ({
      ...prev,
      [idString]: true
    }));
  }, []);

  // Unhide a row (for "add" type impacts that are accepted)
  const unhideRow = useCallback((solModId) => {
    const idString = String(solModId);
    setHiddenRows(prev => {
      const newHiddenRows = { ...prev };
      delete newHiddenRows[idString];
      return newHiddenRows;
    });
  }, []);

  // Check if a row is hidden
  const isRowHidden = useCallback((solModId) => {
    const idString = String(solModId);
    return hiddenRows[idString] || false;
  }, [hiddenRows]);

  // Clear all hidden rows
  const clearHiddenRows = useCallback(() => {
    setHiddenRows({});
  }, []);

  // Show a removed row (for "remove" type impacts that are accepted)
  const showRemovedRow = useCallback((solModId, refSolModId, rowData) => {
    const idString = String(solModId);
    setRemovedRows(prev => ({
      ...prev,
      [idString]: {
        refSolModId: String(refSolModId),
        rowData: rowData,
        isHighlighted: true
      }
    }));
  }, []);

  // Hide a removed row (for "remove" type impacts that are rejected)
  const hideRemovedRow = useCallback((solModId) => {
    const idString = String(solModId);
    console.log("hideRemovedRow :: ",idString);
    setRemovedRows(prev => {
      const newRemovedRows = { ...prev };
      if (newRemovedRows[idString]) {
        newRemovedRows[idString].isHighlighted = false;
      }
      return newRemovedRows;
    });
  }, []);

  // Remove a removed row completely (when impact is cancelled)
  const removeRemovedRow = useCallback((solModId) => {
    const idString = String(solModId);
    setRemovedRows(prev => {
      const newRemovedRows = { ...prev };
      delete newRemovedRows[idString];
      return newRemovedRows;
    });
  }, []);

  // Check if a removed row should be shown
  const isRemovedRowShown = useCallback((solModId) => {
    const idString = String(solModId);
    return removedRows[idString] || false;
  }, [removedRows]);

  // Get removed row data
  const getRemovedRowData = useCallback((solModId) => {
    const idString = String(solModId);
    return removedRows[idString] || null;
  }, [removedRows]);

  // Clear all removed rows
  const clearRemovedRows = useCallback(() => {
    setRemovedRows({});
  }, []);

  const value = {
    solutionModelList,
    addSolutionModel,
    removeSolutionModel,
    clearSolutionModelList,
    isSolutionModelSelected,
    getSolutionModelImpact,
    getSolutionModelList,
    previousValues,
    setPreviousValuesData,
    togglePreviousValues,
    shouldShowPreviousValues,
    getPreviousValue,
    lastImpactedId,
    setImpactHighlight,
    // --- Hidden rows for "add" type impacts ---
    hiddenRows,
    hideRow,
    unhideRow,
    isRowHidden,
    clearHiddenRows,
    // --- Removed rows for "remove" type impacts ---
    removedRows,
    showRemovedRow,
    hideRemovedRow,
    removeRemovedRow,
    isRemovedRowShown,
    getRemovedRowData,
    clearRemovedRows,
    // --- Highlighted sol_mod_ids for review change (not impact) ---
    highlightSolModIds,
    setHighlightSolModIds,
    // --- Highlighted sol_mod_ids for review change (not impact) ---
  };

  return (
    <SolutionModelContext.Provider
      value={{
        solutionModelList,
        addSolutionModel,
        removeSolutionModel,
        clearSolutionModelList,
        isSolutionModelSelected,
        getSolutionModelImpact,
        getSolutionModelList,
        previousValues,
        setPreviousValuesData,
        showPreviousValues,
        togglePreviousValues,
        shouldShowPreviousValues,
        getPreviousValue,
    setImpactHighlight,
    clearHighlights,
    lastImpactedId,
    // --- Hidden rows for "add" type impacts ---
    hiddenRows,
    hideRow,
    unhideRow,
    isRowHidden,
        clearHiddenRows,
        // --- Removed rows for "remove" type impacts ---
        removedRows,
        showRemovedRow,
        hideRemovedRow,
        removeRemovedRow,
        isRemovedRowShown,
        getRemovedRowData,
        clearRemovedRows,
        // --- Highlighted sol_mod_ids for review change (not impact) ---
        highlightSolModIds,
        setHighlightSolModIds,
        // --- Highlighted sol_mod_ids for review change (not impact) ---
      }}
    >
      {children}
    </SolutionModelContext.Provider>
  );
}; 